import moment from "moment"
import { createSelector } from "reselect"
import { get, mapValues, keyBy, includes, chain, find, map, isEmpty } from "lodash"
import { BETA_CATEGORIES } from "helpers/analyzeConstants"
import { computeSort, computeMetricTypeOptions, computeSelectedMetricType } from "helpers/assets"
import { getSortedContentSets } from "selectors/contentSets"
import { getMissingDataFiles, betasAreMissing } from "selectors/missingDataFiles"
import { getEarliestDate, getLatestDate } from "modules/portfolio-root/selectors/dates"
import { getSelectedBase } from "modules/portfolio-root/selectors/base"
import { getBetaInstrumentMeta } from "modules/edit-portfolio/selectors"
import { getSelectedDate as getExperimentSelectedDate } from "modules/edit-experiment/selectors/dates"
import { getIsOpPro } from "modules/account-root/selectors"
import { DATE_FORMAT } from "helpers/dates"
import { getContentSetCategories, getContentSetFactors, getSelectedContentSet } from "./exposure"
import { getBetaFactors } from "./beta"
import { getForecast } from "./forecast"
import { TOOLTIPS } from "../helpers/tooltips"
import { getIsActiveAndNotIntraday, getIsIntraday } from "./intraday"
import { getHistoricalFactorSensitivityUnavailable } from "./factorSensitivity/historical"
import { getBaseMetricTypeOptions } from "selectors/assets/metricTypes"
import { getModelFactors, getModelCategories } from "selectors/modelMetadata"

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []
export const ITEMS_PER_PAGE = 200

export const getSelectedPage = state => get(state, "modules.rebalance.page")
export const getAccountId = state => get(state, "global.selected.accountId")
const getRawOrder = state => get(state, "modules.rebalance.order")
export const getSearchResults = state => get(state, "modules.rebalance.searchResults")

const getRawDecompositionType = state => get(state, "modules.rebalance.decompositionType")
export const getSelectedDataType = state => get(state, "modules.rebalance.selectedDataType", "original")
export const getWidgetField = state => get(state, "modules.rebalance.widgetField")
const getRunningQueries = state => get(state, "modules.rebalance.runningQueries")
export const getActionsBase = state => get(state, "modules.rebalance.actionsBase")
export const getSimulateActions = state => get(state, "modules.rebalance.simulateActions")
export const getReset = state => get(state, "modules.rebalance.reset")
const getMetricType = state => get(state, "modules.rebalance.metricType")
const getDefaultMetricUnits = state => get(state, "modules.rebalance.defaultMetricUnits")
const getRebalanceDateRaw = state => get(state, "modules.rebalance.rebalanceDate")

export const getIsRebalanceRunning = createSelector(
  [getRunningQueries],
  (queries) => !isEmpty(queries),
)

export const getRawSort = state => get(state, "modules.rebalance.order")
export const getIdSort = createSelector(
  [getRawSort],
  computeSort,
)

export const getRebalanceMetrics = createSelector(
  [getMissingDataFiles, getSortedContentSets, getBetaInstrumentMeta, getIsOpPro, getHistoricalFactorSensitivityUnavailable],
  (missingDataFiles, contentSets, betaInstrument, isOpPro, historicalFactorSensitivityUnavailable) => {
    const betasDisabled = betasAreMissing(missingDataFiles) && !betaInstrument
    return chain([
      {
        id: "risk",
        label: "Risk Decomposition (%)",
        tooltip: isOpPro ? "Only available with enterprise plan" : TOOLTIPS.decomposition.risk,
        groupId: "model",
        groupLabel: "Risk model",
        disabled: isOpPro,
      },
      {
        id: "exposure",
        label: "Exposure Contributors",
        tooltip: TOOLTIPS.decomposition.exposure,
        groupId: "model",
        groupLabel: "Risk model",
      },
      {
        id: "beta",
        label: "Beta Contributors",
        disabled: betasDisabled,
        tooltip: betasDisabled ? "There are no Predicted or Historical beta values for the selected model" : TOOLTIPS.decomposition.beta,
        groupId: "model",
        groupLabel: "Risk model",
      },
      {
        id: "marginal",
        label: "Marginal Contribution",
        disabled: isOpPro,
        tooltip: isOpPro ? "Only available with enterprise plan" : TOOLTIPS.decomposition.marginal,
        groupId: "model",
        groupLabel: "Risk model",
      },
      {
        id: "predictedFactorSensitivity",
        label: "Predicted Factor Sensitivity",
        disabled: isOpPro,
        tooltip: isOpPro ? "Only available with enterprise plan" : TOOLTIPS.decomposition.predictedFactorSensitivity,
        groupId: "factorSensitivity",
        groupLabel: "Factor Sensitivity",
      },
      {
        id: "historicalFactorSensitivity",
        label: "Historical Factor Sensitivity",
        groupId: "factorSensitivity",
        groupLabel: "Factor Sensitivity",
        disabled: Boolean(historicalFactorSensitivityUnavailable),
        tooltip: historicalFactorSensitivityUnavailable || TOOLTIPS.decomposition.historicalFactorSensitivity,
      },
      ...map(contentSets, ({ escapedId, name, description }) => ({
        id: escapedId,
        label: name,
        tooltip: description,
        groupId: "contentSets",
        groupLabel: "Content Sets",
      })),
    ])
      .compact()
      // We can simply use the array index as the order since arrays preserve declaration order
      .map((item, index) => ({ ...item, order: index + 1 }))
      .value()
  },
)

export const getDecompositionType = createSelector(
  [getRawDecompositionType, getRebalanceMetrics],
  (decompositionType, metrics) => !decompositionType ? chain(metrics)
    .filter(({ disabled }) => !disabled)
    .first()
    .get("id")
    .value() : decompositionType,
)

export const getIsContentSet = createSelector(
  [getDecompositionType, getSortedContentSets],
  (decompositionType, contentSets) => Boolean(find(contentSets, { escapedId: decompositionType })),
)

export const getOrder = createSelector(
  [getRawOrder, getIsActiveAndNotIntraday],
  (order, isActive) => {
    if (!isEmpty(order)) { return order }
    return isActive
      ? { id: "activePercentEquity", direction: "desc" }
      : { id: "positionPercentEquity", direction: "desc" }
  },
)

export const getAllRawFactorsCategories = createSelector(
  [getModelCategories, getContentSetCategories],
  (categories, contentSetCategories) => ([
    ...(map(categories, category => ({ ...category, isRiskModelCategory: true })) || EMPTY_ARRAY),
    ...BETA_CATEGORIES,
    ...(contentSetCategories || EMPTY_OBJECT),
  ]),
)

export const getFactorsData = createSelector(
  [getModelFactors, getContentSetFactors, getBetaFactors],
  (rawFactors, contentSetFactors, betaFactors) => ({
    model: rawFactors,
    beta: betaFactors,
    ...mapValues(contentSetFactors, item => keyBy(item, "id")),
  }),
)

export const getIsModelFactorsUsed = createSelector(
  [getDecompositionType],
  (decompositionType) => includes(["risk", "exposure", "predictedFactorSensitivity", "historicalFactorSensitivity"], decompositionType),
)

export const getRebalanceDate = createSelector(
  [getRebalanceDateRaw, getEarliestDate, getLatestDate, getExperimentSelectedDate, getIsIntraday],
  (rebalanceDate, earliestDate, latestDate, experimentSelectedDate, isIntraday) => {
    if (isIntraday) {
      return moment().format(DATE_FORMAT)
    }

    if (rebalanceDate?.selectedDate) {
      return rebalanceDate.selectedDate
    }

    if (experimentSelectedDate && experimentSelectedDate <= latestDate && experimentSelectedDate >= earliestDate) {
      return experimentSelectedDate
    }
    return latestDate
  })

export const getSimulatorSettings = createSelector(
  [getWidgetField, getForecast],
  (widgetField, forecast) => ({
    widgetField,
    forecast,
  }),
)

export const getIsActiveTradingDisabled = createSelector(
  [getSelectedBase, getActionsBase],
  (selectedBase, actionsBase) => actionsBase && get(selectedBase, "id") !== get(actionsBase, "id"),
)

const getAssetType = () => "portfolio"

const getSelectedContentSetDataType = createSelector(
  [getSelectedContentSet],
  (contentSet) => get(contentSet, "dataType"),
)
export const getMetricTypeOptions = createSelector(
  [getBaseMetricTypeOptions, getDecompositionType, getIsContentSet, getAssetType, getSelectedContentSetDataType, getDefaultMetricUnits],
  computeMetricTypeOptions,
)

export const getSelectedMetricType = createSelector(
  [getMetricType, getMetricTypeOptions],
  computeSelectedMetricType,
)
